import InlineNotification from '@components/shared/InlineNotification'
import store from 'stores/store'
import SwapTokenSelect from './SwapTokenSelect'
import { walletBalanceForToken } from 'utils/tokens'
import {
  floorToDecimal,
  formatCurrencyValue,
  formatNumericValue,
} from 'utils/numbers'
import { useCallback, useMemo, useState } from 'react'
import SheenLoader from '@components/loading/SheenLoader'
import SearchModal from '@components/modals/SearchModal'
import { useWalletTokens } from 'hooks/useWalletTokens'
import FormatNumericValue from '@components/shared/FormatNumericValue'
import WalletIcon from '@components/icons/WalletIcon'
import { TokenMetadataItem } from 'types/token'
import { useLatestBuyTokenPrice } from './useLatestTokenPrice'
import { useTranslation } from 'next-i18next'

const BuyInput = ({
  outAmount,
  loading,
  error,
}: {
  outAmount: number
  loading: boolean
  error?: string
}) => {
  const { t } = useTranslation('swap')
  const { walletTokens } = useWalletTokens()
  const amountInFormValue = store((s) => s.swap.amountIn)
  const outputToken = store((s) => s.swap.outputToken)
  const [showSelectList, setShowSelectList] = useState(false)
  const { lastPrice } = useLatestBuyTokenPrice(outputToken.mint)

  const [walletBalance, buyDecimals] = useMemo(() => {
    if (!outputToken.mint) return ['0', 6]
    const walletBalance = walletBalanceForToken(walletTokens, outputToken.mint)
    const max = floorToDecimal(
      walletBalance.balance,
      walletBalance.decimals,
    ).toFixed()
    return [max, walletBalance.decimals]
  }, [walletTokens, outputToken.mint])

  const buyAmountDisplay = useMemo(() => {
    if (!amountInFormValue) return 0
    return outAmount ? outAmount : 0
  }, [amountInFormValue, outAmount])

  const handleSelect = useCallback(
    async (newOutputMint: string, newOutputToken?: TokenMetadataItem) => {
      void store
        .getState()
        .actions.setNewOutputToken(newOutputMint, newOutputToken)

      setShowSelectList(false)
    },
    [],
  )

  return (
    <>
      <div className={`grid grid-cols-2 rounded-t-xl`}>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <p className="text-th-fgd-1">{t('buy')}</p>
          <div className="flex items-center text-th-fgd-4">
            <WalletIcon className="mr-1 size-3" />
            <span className="font-mono text-xs">
              <FormatNumericValue
                value={walletBalance}
                decimals={buyDecimals}
              />
            </span>
          </div>
        </div>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <SwapTokenSelect
            imageUrl={outputToken.image_url}
            symbol={outputToken.symbol}
            showList={setShowSelectList}
          />
        </div>
        <div className="col-span-2 flex h-10 items-center justify-between">
          {loading ? (
            <>
              <SheenLoader>
                <div className={`h-7 w-44 bg-th-bkg-2`} />
              </SheenLoader>
              <SheenLoader>
                <div className={`h-3.5 w-12 bg-th-bkg-2`} />
              </SheenLoader>
            </>
          ) : (
            <>
              <p
                className={`font-mono text-xl ${
                  buyAmountDisplay ? 'text-th-fgd-1' : 'text-th-fgd-4'
                }`}
              >
                {buyAmountDisplay
                  ? formatNumericValue(buyAmountDisplay, outputToken.decimals)
                  : '0.00'}
              </p>
              <p className="font-mono text-xs text-th-fgd-4">
                {buyAmountDisplay
                  ? lastPrice?.price
                    ? formatCurrencyValue(buyAmountDisplay * lastPrice.price)
                    : '–'
                  : '$0.00'}
              </p>
            </>
          )}
        </div>
        {error ? (
          <div className="col-span-2 mt-1 flex justify-center">
            <InlineNotification
              type="error"
              desc={error}
              hideBorder
              hidePadding
            />
          </div>
        ) : null}
      </div>
      {showSelectList ? (
        <SearchModal
          isOpen={showSelectList}
          onClose={() => setShowSelectList(false)}
          onResultClick={handleSelect}
          type="outputToken"
        />
      ) : null}
    </>
  )
}

export default BuyInput
